import { calculationTimeStamp, formattedTimeNotFilter } from 'utils/calculateTime';
import { changeMobileForm } from 'utils/changeMobileForm';
import { ViewDetailDiagnosisPageInfoProps } from 'models/Hospital/viewDetailDiagnosisPageTypes';
import { CellProps, ViewDetailPatientPageInfoBodyProps } from 'models/Hospital/viewDetailPatientPageTypes';
import moment from 'moment';
import styled from 'styled-components';
import { font, palette } from 'styles/Global/globalStyles';
import ProcessCheckBar from './ProcessCheckBar';
import { calculateAge } from 'utils/calculateAge';
import { OutlineGrayXS, SquareLineSBadge } from 'components/Common/Badge';
import { Body2Medium, Body3Medium, Body5Regular, Title1_Bold } from 'styles/Global/typography';
import { TextColor } from 'styles/Global/colorSemantic';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import { getConsultationTypeStr, getRelationTypeStr } from 'utils/hospitalUtils';
import { useLoginStore } from 'store/useLoginDataStore';
import { ColumnDivider } from 'styles/Global/commonStyles';
import { AccountLevel } from 'utils/statusCode';
import NoShowToolTip from 'components/ToolTip/NoShowToolTip';
import useToggle from 'hooks/CustomHooks/useToggle';

export const ViewDetailPatientPageInfoBody = ({ userInfo, birthDate, age }: ViewDetailPatientPageInfoBodyProps) => {
    const { gender, ageString } = calculateAge(userInfo?.identityNumber || '');
    return (
        <>
            <InfoHeadContainer>
                <SquareLineSBadge>{userInfo?.regNum}</SquareLineSBadge>
                <NameAndGenderContainer>
                    <span>{userInfo?.name}</span>
                    {gender === 'male' ? <GenderMan $width='24px' $height='24px' /> : <GenderWoman $height='24px' $width='24px' />}
                </NameAndGenderContainer>
                <BirthDateAndAgeWrapper>
                    <BirthDate>{birthDate}</BirthDate>
                    <Age>{`(${ageString})`}</Age>
                </BirthDateAndAgeWrapper>
            </InfoHeadContainer>
            <InfoBodyLayout>
                <InfoBodyContainer>
                    <CellsContainer>
                        <CellsWrapper>
                            <TitleCell>현재 나이</TitleCell>
                            <BodyCell>{age}</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>연락처</TitleCell>
                            <BodyCell>{changeMobileForm(userInfo?.mobile || '')}</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>환자 ID</TitleCell>
                            <PatientIdCell>{userInfo.patientCode || '-'}</PatientIdCell>
                            {/* <BodyCell>{diagnosisInfo.patientCode || '-'}</BodyCell> */}
                        </CellsWrapper>
                    </CellsContainer>
                    <CellsContainer>
                        <CellsWrapper>
                            <TitleCell>마지막 진료일시</TitleCell>
                            <BodyCell>{calculationTimeStamp(userInfo?.lastDiagnosisTimestamp || 0)}</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>총 진료 건수</TitleCell>
                            <BodyCell>{userInfo?.diagnosisIdList.length}건의 진료</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>환자 등록일</TitleCell>
                            <BodyCell>{moment(userInfo?.timestamp).format('YYYY/MM/DD')}</BodyCell>
                        </CellsWrapper>
                    </CellsContainer>
                </InfoBodyContainer>
                <CellsWrapper $marginTop='14px'>
                    <TitleCell>메모</TitleCell>
                    <BodyCell $width='612px'>{userInfo?.patientMemo || '-'}</BodyCell>
                </CellsWrapper>
            </InfoBodyLayout>
        </>
    );
};

export const ViewDetailDiagnosisPageInfoBody = ({ userInfo, diagnosisInfo, onClick, noShowDisabled }: ViewDetailDiagnosisPageInfoProps) => {
    const [onNoShowToolTip, setOnNoShowToolTip] = useToggle();

    const { birthDate, ageString, gender } = calculateAge(userInfo?.identityNumber || '');
    const { loginData } = useLoginStore();

    // console.log(diagnosisInfo.assessmentAge, ageString, '1233123213');

    return (
        <>
            <InfoHeadContainer>
                {loginData.level !== AccountLevel.externalCounselor && <SquareLineSBadge>{userInfo?.regNum}</SquareLineSBadge>}
                <NameAndGenderContainer>
                    <span>{userInfo?.name}</span>
                    {gender === 'male' ? <GenderMan $width='24px' $height='24px' /> : <GenderWoman $height='24px' $width='24px' />}
                </NameAndGenderContainer>
                <BirthDateAndAgeWrapper>
                    <BirthDate>{birthDate}</BirthDate>
                    <Age>{`(${ageString})`}</Age>
                </BirthDateAndAgeWrapper>
                <ProcessCheckBar userInfo={userInfo} diagnosisInfo={diagnosisInfo} onClick={onClick} $height='40px' $width='200px' $top='44px' $left='-10px' />
            </InfoHeadContainer>
            <InfoBodyLayout>
                <InfoBodyContainer>
                    <CellsContainer>
                        <CellsWrapper>
                            <TitleCell>학령/학기</TitleCell>
                            <BodyCell>{diagnosisInfo.gradeName}</BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>검사 나이</TitleCell>
                            {loginData.level === AccountLevel.externalCounselor ? <BodyCell>{ageString || '-'}</BodyCell> : <BodyCell>{diagnosisInfo.assessmentAge || '-'}</BodyCell>}
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>연락처</TitleCell>
                            <BodyCell>{changeMobileForm(userInfo?.mobile || diagnosisInfo.patientMobile)}</BodyCell>
                        </CellsWrapper>
                    </CellsContainer>
                    <CellsContainer>
                        <CellsWrapper>
                            <TitleCell>{`${getConsultationTypeStr(loginData.level)} 일시`}</TitleCell>
                            <BodyCell $noShowDisabled={noShowDisabled}>
                                <span className='textDecoration'>{formattedTimeNotFilter(diagnosisInfo.diagnosisDateTime)}</span>
                                {diagnosisInfo.noShowInfo?.length !== 0 && (
                                    <div onMouseEnter={setOnNoShowToolTip} onMouseLeave={setOnNoShowToolTip} style={{ position: 'relative' }}>
                                        <OutlineGrayXS $type='red' $width='59px' $top='1px' $right='-12px'>
                                            노쇼 {diagnosisInfo.noShowInfo?.length}회{onNoShowToolTip && <NoShowToolTip diagnosisInfo={diagnosisInfo} $left='-10px' />}
                                        </OutlineGrayXS>
                                    </div>
                                )}
                            </BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            <TitleCell>{loginData.level === AccountLevel.externalCounselor ? '담당자' : '담당의'}</TitleCell>
                            <BodyCell>
                                {diagnosisInfo.doctorName} <DoctorText>{loginData.level !== AccountLevel.externalCounselor ? '원장님' : ''}</DoctorText>
                            </BodyCell>
                        </CellsWrapper>
                        <CellsWrapper>
                            {loginData.level === AccountLevel.externalCounselor ? (
                                <>
                                    <TitleCell>주양육자</TitleCell>
                                    <PatientIdCell>
                                        {getRelationTypeStr(diagnosisInfo.relationType) || '-'} <ColumnDivider $height='12px' /> {diagnosisInfo.parentName || '-'}
                                    </PatientIdCell>
                                </>
                            ) : (
                                <>
                                    <TitleCell>환자 ID</TitleCell>
                                    <PatientIdCell>{userInfo.patientCode || '-'}</PatientIdCell>
                                </>
                            )}
                        </CellsWrapper>
                    </CellsContainer>
                </InfoBodyContainer>
                <CellsWrapper $marginTop='14px'>
                    <TitleCell>{loginData.level === AccountLevel.externalCounselor ? '검사 동기' : '메모'}</TitleCell>
                    <BodyCell $width='612px'>{loginData.level !== AccountLevel.externalCounselor ? userInfo?.patientMemo || '-' : diagnosisInfo.counselReason || '-'}</BodyCell>
                </CellsWrapper>
            </InfoBodyLayout>
        </>
    );
};

const InfoHeadContainer = styled.div`
    display: flex;
    min-width: 240px;
    flex-direction: column;
`;

const NameAndGenderContainer = styled(Title1_Bold)`
    display: flex;
    align-items: center;
    height: 34px;
    margin-top: 6px;
    margin-bottom: 2px;
    gap: 4px;
`;

const BirthDate = styled(Body2Medium)`
    color: ${TextColor.color_text_neutral};
`;

const BirthDateAndAgeWrapper = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 14px;
`;

const Age = styled(Body5Regular)`
    display: flex;
    align-items: center;
    color: ${TextColor.color_text_light};
`;

const InfoBodyLayout = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 568px;
    max-width: 780px;
    // height: 100%;
    padding: 8px 0 14px 0;
`;
const InfoBodyContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100px;
`;

const CellsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 50%;
    height: 100%;
    gap: 14px;
`;

const CellsWrapper = styled.div<CellProps>`
    display: flex;
    width: 100%;
    gap: 4px;
    margin-top: ${(props) => props.$marginTop || '0px'};
`;

const TitleCell = styled.span`
    width: 120px;
    height: 100%;
    line-height: 24px;
    font-size: ${font.size[16]};
    font-weight: ${font.weight.regular};
    color: ${palette.gray};
`;

const BodyCell = styled(Body3Medium)<CellProps>`
    display: flex;
    align-items: center;
    gap: 4px;
    width: ${(props) => props.$width || '256px'};
    // max-width: ${(props) => props.$width};
    color: ${(props) => (props.$noShowDisabled ? TextColor.color_text_light : TextColor.color_text_neutral)};

    ${(props) =>
        props.$noShowDisabled &&
        `& .textDecoration {
            text-decoration: line-through;
            -webkit-text-decoration: line-through;
            text-decoration-line: line-through;
            text-decoration-style: solid;
        }`}

    white-space: pre-wrap; /* CSS3 */
    white-space: -moz-pre-wrap; /* Firefox */
    white-space: -pre-wrap; /* Opera <7 */
    white-space: -o-pre-wrap; /* Opera 7 */
    word-wrap: break-word; /* Internet Explorer */
    overflow-wrap: break-word; /* CSS3 */
`;

const PatientIdCell = styled(Body3Medium)<CellProps>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: ${(props) => props.$width || '220px'};
    color: ${TextColor.color_text_neutral};
    white-space: pre-wrap; /* CSS3 */
    white-space: -moz-pre-wrap; /* Firefox */
    white-space: -pre-wrap; /* Opera <7 */
    white-space: -o-pre-wrap; /* Opera 7 */
    word-wrap: break-word; /* Internet Explorer */
    overflow-wrap: break-word; /* CSS3 */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DoctorText = styled.span`
    font-size: ${font.size[15]};
    font-weight: ${font.weight.regular};
    color: ${palette.gray};
`;
